import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

type ParamValue = string | number | boolean;

type NullableParam = ParamValue | null | undefined;

export interface UseListSearchParamsOptions {
  pageParam?: string;
  pageSizeParam?: string;
  orderingParam?: string;
  defaultPage?: number;
  defaultPageSize?: number;
  defaultOrdering?: string;
  filters?: string[];
  basePath?: string;
}

interface SetParamsOptions {
  resetPage?: boolean;
}

const applyOverrides = (params: URLSearchParams, overrides: Record<string, NullableParam>) => {
  Object.entries(overrides).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      params.delete(key);
    } else {
      params.set(key, String(value));
    }
  });
};

/**
 * Sync list pagination, ordering, and filters with URL search params.
 */
export function useListSearchParams(options: UseListSearchParamsOptions = {}) {
  const {
    pageParam = 'page',
    pageSizeParam = 'page_size',
    orderingParam = 'ordering',
    defaultPage = 1,
    defaultPageSize = 10,
    defaultOrdering = '',
    filters: filterKeys = [],
    basePath,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const effectiveBasePath = basePath ?? location.pathname;

  const rawPage = Number(searchParams.get(pageParam) ?? defaultPage);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage - 1 : defaultPage - 1;

  const rawRows = Number(searchParams.get(pageSizeParam) ?? defaultPageSize);
  const rowsPerPage = Number.isFinite(rawRows) && rawRows > 0 ? rawRows : defaultPageSize;

  const ordering = searchParams.get(orderingParam) ?? defaultOrdering;

  const filters = useMemo(() => {
    const result: Record<string, string | null> = {};
    filterKeys.forEach((key) => {
      result[key] = searchParams.get(key);
    });
    return result;
  }, [filterKeys, searchParams]);

  const mutateParams = useCallback(
    (modifier: (params: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        modifier(next);
        return next;
      });
    },
    [setSearchParams],
  );

  const setParam = useCallback(
    (key: string, value: NullableParam, options?: SetParamsOptions) => {
      mutateParams((params) => {
        applyOverrides(params, { [key]: value });
        if (options?.resetPage) {
          params.set(pageParam, '1');
        }
      });
    },
    [mutateParams, pageParam],
  );

  const setParams = useCallback(
    (overrides: Record<string, NullableParam>, options?: SetParamsOptions) => {
      mutateParams((params) => {
        applyOverrides(params, overrides);
        if (options?.resetPage) {
          params.set(pageParam, '1');
        }
      });
    },
    [mutateParams, pageParam],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      mutateParams((params) => {
        const normalized = Math.max(0, nextPage) + 1;
        params.set(pageParam, normalized.toString());
      });
    },
    [mutateParams, pageParam],
  );

  const setRowsPerPage = useCallback(
    (nextRowsPerPage: number) => {
      mutateParams((params) => {
        params.set(pageSizeParam, String(nextRowsPerPage));
        params.set(pageParam, '1');
      });
    },
    [mutateParams, pageParam, pageSizeParam],
  );

  const setOrdering = useCallback(
    (value: NullableParam) => {
      setParam(orderingParam, value);
    },
    [orderingParam, setParam],
  );

  const buildHref = useCallback(
    (overrides: Record<string, NullableParam> = {}) => {
      const next = new URLSearchParams(searchParams);
      applyOverrides(next, overrides);
      const queryString = next.toString();
      return `${effectiveBasePath}${queryString ? `?${queryString}` : ''}`;
    },
    [searchParams, effectiveBasePath],
  );

  const getPageLink = useCallback(
    (targetPage: number) => buildHref({ [pageParam]: Math.max(0, targetPage) + 1 }),
    [buildHref, pageParam],
  );

  return {
    searchParams,
    filters,
    page,
    rowsPerPage,
    ordering,
    setParam,
    setParams,
    setPage,
    setRowsPerPage,
    setOrdering,
    buildHref,
    getPageLink,
    basePath: effectiveBasePath,
    pageParam,
    pageSizeParam,
    orderingParam,
  } as const;
}
